import { useEffect, useRef, useState } from 'react';
import {
    BuildingOffice2Icon,
    ChevronDownIcon,
    MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { usePracticeSearch } from '../../../hooks/usePracticeSearch';

export interface SelectedPractice {
    id: string;
    name: string;
}

interface PracticeFilterPillProps {
    selected: SelectedPractice | null;
    onChange: (practice: SelectedPractice | null) => void;
}

// Single-select practice filter for the Users list toolbar. Outer chrome
// mirrors FilterDropdown / CategoryFilterDropdown exactly at every state (closed,
// active, open pill treatment, dropdown panel, search input, row hover) so the
// toolbar reads as one family. Unlike FilterDropdown (a static option list),
// the practice directory is too large to hand over in one shot, so the option
// rows are server-searched (via usePracticeSearch) as the user types.
export function PracticeFilterPill({ selected, onChange }: PracticeFilterPillProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { results, isLoading } = usePracticeSearch(search, open);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [open]);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    const isActive = selected !== null;

    const handlePick = (practice: SelectedPractice) => {
        onChange(selected?.id === practice.id ? null : practice);
        setOpen(false);
        setSearch('');
    };

    const handleClear = () => {
        onChange(null);
        setOpen(false);
    };

    return (
        <div className="relative inline-block text-left" ref={containerRef}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                aria-haspopup="listbox"
                style={{ minWidth: '160px' }}
                className={`inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300 w-full ${
                    open ? 'border-gray-700' : 'border-gray-300'
                }`}
            >
                <span>Practice</span>
                <span className="flex items-center ml-2">
                    <span
                        className={`mr-1 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs leading-none transition-opacity duration-200 ${
                            isActive ? 'bg-gray-700 text-white opacity-100' : 'opacity-0'
                        }`}
                    >
                        {isActive ? 1 : 0}
                    </span>
                    <ChevronDownIcon
                        className={`h-5 w-5 transition-transform duration-300 ${open ? 'transform rotate-180' : ''}`}
                    />
                </span>
            </button>

            <div
                className={`absolute left-1/2 -translate-x-1/2 mt-2 w-72 z-20 bg-white shadow-lg rounded-2xl transition-all duration-300 ease-in-out overflow-hidden ring-2 ${
                    open ? 'ring-gray-700' : 'ring-transparent'
                }`}
                style={{ maxHeight: open ? '24rem' : '0', opacity: open ? 1 : 0 }}
            >
                <div className="sticky top-0 bg-white px-3 pt-3 pb-2 flex items-center justify-between">
                    <span className="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                        Practice
                    </span>
                    {isActive && (
                        <button
                            type="button"
                            className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                            onClick={handleClear}
                        >
                            clear
                        </button>
                    )}
                </div>
                <div className="px-3 pb-2">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            ref={inputRef}
                            type="text"
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                            placeholder="Search a practice"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="overflow-y-auto scrollbar-hide pb-1" style={{ maxHeight: '16rem' }}>
                    {isLoading ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                            Searching...
                        </div>
                    ) : results.length === 0 ? (
                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                            No matches
                        </div>
                    ) : (
                        results.map((practice) => {
                            const checked = selected?.id === practice.id;
                            return (
                                <button
                                    key={practice.id}
                                    type="button"
                                    onClick={() =>
                                        handlePick({ id: practice.id, name: practice.name })
                                    }
                                    className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-left hover:bg-gray-100"
                                >
                                    <BuildingOffice2Icon className="h-4 w-4 text-gray-400 shrink-0" />
                                    <span className="min-w-0 flex-1 truncate text-gray-700 select-none">
                                        {practice.name}
                                    </span>
                                    {checked && (
                                        <span className="h-1.5 w-1.5 rounded-full bg-gray-700 shrink-0" />
                                    )}
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
